import React from "react";
import {
  Box,
  Container,
  Typography,
  Link,
  IconButton,
  Stack,
  Divider,
} from "@mui/material";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import {
  MdEmail,
  MdPhone,
  MdLocationOn,
} from "react-icons/md";
import {
  FiHome,
  FiArrowUpRight,
} from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";

const PRIMARY = "#ED213A";
const DARK = "#202124";
const MUTED = "#6B7280";

const Footer = () => {
  const year = new Date().getFullYear();

  const columns = [
    {
      title: "Explore",
      links: [

        { label: "Post Your Property", href: "/post-properties/basic-info" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About Us", href: "/aboutUs" },
        { label: "Contact Us", href: "/contactUs" },
        { label: "Blogs", href: "/blogs" },
        { label: "Careers", href: "/careers" },
       ],
    },
    {
      title: "Quick Links",
      links: [
        { label: "My Account", href: "/login" },
        { label: "Privacy Policy", href: "/privacy-policy" },
        { label: "Terms & Conditions", href: "/terms-and-conditions" },
      ],
    },
  ];

  const socials = [
    {
      label: "Facebook",
      href: "https://facebook.com/Gharnmakaan",
      icon: <FaFacebookF />,
    },
    {
      label: "Instagram",
      href: "https://instagram.com/gharnmakaan.c/",
      icon: <FaInstagram />,
    },
    {
      label: "X",
      href: "https://twitter.com",
      icon: <FaXTwitter />,
    },
    {
      label: "LinkedIn",
      href: "https://linkedin.com",
      icon: <FaLinkedinIn />,
    },
    {
      label: "YouTube",
      href: "https://youtube.com/@Gharnmakaan",
      icon: <FaYoutube />,
    },
  ];

  const linkSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    width: "fit-content",
    color: MUTED,
    fontSize: "0.9rem",
    textDecoration: "none",
    lineHeight: 1.7,
    transition: "color 0.2s ease, transform 0.2s ease",
    "&:hover": {
      color: PRIMARY,
      transform: "translateX(3px)",
    },
  };

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        overflow: "hidden",
        bgcolor: "#FFFFFF",
        color: DARK,
        borderTop: "1px solid #F0F0F0",
        pt: { xs: 5, md: 7 },
        pb: 2.5,
      }}
    >
      {/* Subtle brand accent */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          top: 0,
          left: { xs: "8%", md: "5%" },
          width: 90,
          height: 3,
          bgcolor: PRIMARY,
          borderRadius: "0 0 4px 4px",
        }}
      />

      <Container maxWidth="xl">
        {/* Main footer columns */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              md: "1.55fr 1fr 1fr 1fr",
            },
            columnGap: { xs: 3, md: 5, lg: 8 },
            rowGap: { xs: 4, md: 3 },
            mb: 5,
          }}
        >
          {/* Brand */}
          <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.3}
              sx={{ mb: 2.2 }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "12px",
                  bgcolor: PRIMARY,
                  color: "#FFFFFF",
                  fontSize: 23,
                  boxShadow: "0 5px 14px rgba(237,33,58,0.18)",
                }}
              >
                <FiHome />
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: DARK,
                    fontSize: "1.3rem",
                    fontWeight: 850,
                    letterSpacing: "-0.7px",
                    lineHeight: 1.2,
                  }}
                >
                  Gharnmakaan
                  <Box component="span" sx={{ color: PRIMARY }}>
                    .com
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    mt: 0.4,
                    color: "#929292",
                    fontSize: "0.68rem",
                    letterSpacing: 1.6,
                    textTransform: "uppercase",
                  }}
                >
                  Find your place
                </Typography>
              </Box>
            </Stack>

            <Typography
              sx={{
                color: MUTED,
                fontSize: "0.9rem",
                lineHeight: 1.9,
                maxWidth: 340,
                mb: 2.8,
              }}
            >
              Your next home starts here. Discover properties for buying,
              selling, and renting with Gharnmakaan.
            </Typography>

            <Stack spacing={1.6}>
              <Stack direction="row" spacing={1.3} alignItems="center">
                <MdPhone size={19} color={PRIMARY} />
                <Link href="tel:+911203698141" sx={linkSx}>
                  +91 1203698141
                </Link>
              </Stack>

              <Stack direction="row" spacing={1.3} alignItems="center">
                <MdEmail size={19} color={PRIMARY} />
                <Link
                  href="mailto:support@gharnmakaan.com"
                  sx={{ ...linkSx, overflowWrap: "anywhere" }}
                >
                  support@gharnmakaan.com
                </Link>
              </Stack>

              <Stack direction="row" spacing={1.3} alignItems="flex-start">
                <MdLocationOn
                  size={20}
                  color={PRIMARY}
                  style={{ flexShrink: 0, marginTop: 2 }}
                />
                <Typography
                  sx={{
                    color: MUTED,
                    fontSize: "0.9rem",
                    lineHeight: 1.7,
                  }}
                >
                  Indirapuram, Ghaziabad, India
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Navigation */}
          {columns.map((column) => (
            <Box key={column.title}>
              <Typography
                sx={{
                  color: DARK,
                  fontSize: "0.92rem",
                  fontWeight: 800,
                  mb: 2.4,
                }}
              >
                {column.title}
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    width: 25,
                    height: 2,
                    bgcolor: PRIMARY,
                    mt: 1,
                    borderRadius: 2,
                  }}
                />
              </Typography>

              <Stack spacing={1.5}>
                {column.links.map((item) => (
                  <Link
                    key={item.label}
                    component={RouterLink}
                    to={item.href}
                    sx={linkSx}
                  >
                    {item.label}
                    <FiArrowUpRight
                      size={13}
                      style={{ opacity: 0.55 }}
                    />
                  </Link>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>

        {/* Social section */}
        <Divider sx={{ borderColor: "#EEEEEE" }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2.5,
            py: 3,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.92rem",
                fontWeight: 750,
                color: DARK,
                mb: 0.5,
              }}
            >
              Follow Gharnmakaan
            </Typography>

            <Typography
              sx={{
                color: MUTED,
                fontSize: "0.84rem",
              }}
            >
              Property updates, ideas, and inspiration.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            {socials.map((social) => (
              <IconButton
                key={social.label}
                component="a"
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                sx={{
                  width: 39,
                  height: 39,
                  borderRadius: "11px",
                  bgcolor: "#F7F7F8",
                  border: "1px solid #EEEEF0",
                  color: "#555B63",
                  transition: "all 0.22s ease",
                  "&:hover": {
                    bgcolor: PRIMARY,
                    color: "#FFFFFF",
                    borderColor: PRIMARY,
                    transform: "translateY(-3px)",
                    boxShadow: "0 5px 12px rgba(237,33,58,0.18)",
                  },
                }}
              >
                {social.icon}
              </IconButton>
            ))}
          </Stack>
        </Box>

        {/* Bottom bar */}
        <Divider sx={{ borderColor: "#EEEEEE" }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 1.5,
            pt: 2.5,
          }}
        >
          <Typography
            sx={{
              color: "#929292",
              fontSize: "0.8rem",
              lineHeight: 1.7,
            }}
          >
            © {year} Gharnmakaan.com. All rights reserved.
          </Typography>

          <Stack direction="row" spacing={2.5}>
            <Link
              component={RouterLink}
              to="/privacy-policy"
              sx={{
                color: "#777777",
                fontSize: "0.8rem",
                textDecoration: "none",
                "&:hover": { color: PRIMARY },
              }}
            >
              Privacy Policy
            </Link>

            <Link
              component={RouterLink}
              to="/terms-and-conditions"
              sx={{
                color: "#777777",
                fontSize: "0.8rem",
                textDecoration: "none",
                "&:hover": { color: PRIMARY },
              }}
            >
              Terms
            </Link>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
